import { StorefrontVehicle, readVehicleCookie, writeVehicleCookie, clearVehicleCookie } from './StorefrontVehicleCookie';
import { showToast } from '../Toast/StorefrontToast';
import { sfUrl } from '../StorefrontPaths';
import {
    GarageVehicle, fetchGarage, saveToGarage, useGarageVehicle, toCookieVehicle, isSameVehicle, garageLabel
} from '../Garage/GarageClient';

/**
 * Storefront vehicle picker. Mounts into a single host element and renders
 * either a "currently shopping for" pill (when sc_vehicle is set) or the
 * cascading YMM dropdowns + VIN field (when no vehicle is selected).
 *
 * Talks to the anonymous /Services/Storefront/Vehicle/* endpoints. No
 * authentication required.
 *
 * Signed-in shoppers (host has data-signed-in="1") also get My Garage (#36990): a Save button
 * in the pill, and their saved vehicles to switch to — a dropdown from the pill, or chips under
 * the picker when no vehicle is selected (a saved vehicle is never selected automatically).
 */
export class VehiclePicker {
    private host: HTMLElement;
    private vehicle: StorefrontVehicle | null = null;
    private isPickerOpen = false;

    private readonly signedIn: boolean;
    /** null until loaded, or when there's no garage (guest, or a non-customer account). */
    private garage: GarageVehicle[] | null = null;
    private garageMax = 10;
    private isGarageOpen = false;
    private isSaving = false;
    private garageError: string | null = null;

    private years: number[] = [];
    private makes: string[] = [];
    private models: string[] = [];

    private selectedYear: number | null = null;
    private selectedMake: string | null = null;
    private selectedModel: string | null = null;

    private isLoadingYears = false;
    private isLoadingMakes = false;
    private isLoadingModels = false;
    private isDecodingVin = false;

    constructor(host: HTMLElement) {
        this.host = host;
        this.vehicle = readVehicleCookie();
        this.signedIn = host.getAttribute('data-signed-in') === '1';
        this.render();

        if (this.signedIn) {
            this.loadGarage();
            window.addEventListener('sc-garage-changed', () => this.loadGarage());
            document.addEventListener('click', e => {
                // composedPath, not contains(): a click inside may have re-rendered (detached) its target.
                if (this.isGarageOpen && !e.composedPath().includes(this.host)) this.closeGarage();
            });
            document.addEventListener('keydown', e => {
                if (this.isGarageOpen && e.key === 'Escape') this.closeGarage();
            });
        }
        // Another widget (e.g. My Garage page) may set or clear the vehicle.
        window.addEventListener('sc-vehicle-changed', () => {
            const current = readVehicleCookie();
            if (JSON.stringify(current) === JSON.stringify(this.vehicle)) return;
            this.vehicle = current;
            this.isPickerOpen = false;
            this.render();
        });
    }

    // ── Render ──────────────────────────────────────────────

    private render(): void {
        if (this.vehicle && !this.isPickerOpen) {
            this.renderPill();
        } else {
            this.renderPicker();
        }
    }

    private renderPill(): void {
        const v = this.vehicle!;
        const saved = this.garage?.find(g => isSameVehicle(v, g)) ?? null;
        const subtitle = [saved?.Nickname, v.trim, v.engine].filter(Boolean).join(' · ');
        const garageButtons = this.garage === null ? '' : `
                ${saved
                    ? `<span class="sf-vehicle-pill-saved" title="Saved in My Garage"><i class="fa fa-check"></i><span> Saved</span></span>`
                    : `<button type="button" class="sf-vehicle-pill-change" data-action="save" aria-label="Save to My Garage"><i class="fa fa-bookmark-o"></i> Save</button>`}
                <button type="button" class="sf-vehicle-pill-change sf-vehicle-pill-garage" data-action="garage" aria-haspopup="true" aria-expanded="${this.isGarageOpen}" aria-label="My Garage">
                    <i class="fa fa-th-list"></i> <span class="sf-vehicle-pill-garage-t">My Garage</span> (${this.garage.length}) <i class="fa fa-caret-down"></i>
                </button>`;
        this.host.innerHTML = `
            <div class="sf-vehicle-pill-wrap">
                <div class="sf-vehicle-pill" role="status" aria-label="Currently shopping for ${this.escapeAttr(`${v.year} ${v.make} ${v.model}`)}">
                    <i class="fa fa-car sf-vehicle-pill-icon"></i>
                    <div class="sf-vehicle-pill-text">
                        <div class="sf-vehicle-pill-line1">${this.escape(`${v.year} ${v.make} ${v.model}`)}</div>
                        ${subtitle ? `<div class="sf-vehicle-pill-line2">${this.escape(subtitle)}</div>` : ''}
                    </div>
                    ${garageButtons}
                    <button type="button" class="sf-vehicle-pill-change" data-action="change" aria-label="Change vehicle">Change</button>
                    <button type="button" class="sf-vehicle-pill-clear" data-action="clear" aria-label="Clear vehicle">&times;</button>
                </div>
                ${this.isGarageOpen ? this.garagePanelHtml(saved) : ''}
            </div>
        `;

        const toggle = () => {
            this.isGarageOpen = !this.isGarageOpen;
            this.garageError = null;
            this.render();
            if (this.isGarageOpen)
                (this.host.querySelector('[data-field="nickname"]') as HTMLInputElement | null)?.focus();
        };
        this.host.querySelector('[data-action="save"]')?.addEventListener('click', toggle);
        this.host.querySelector('[data-action="garage"]')?.addEventListener('click', toggle);
        this.wireGaragePanel();

        this.host.querySelector('[data-action="change"]')?.addEventListener('click', () => {
            this.isPickerOpen = true;
            this.isGarageOpen = false;
            this.selectedYear = this.vehicle?.year ?? null;
            this.selectedMake = this.vehicle?.make ?? null;
            this.selectedModel = this.vehicle?.model ?? null;
            this.render();
            this.loadYears();
        });

        this.host.querySelector('[data-action="clear"]')?.addEventListener('click', () => {
            clearVehicleCookie();
            this.vehicle = null;
            this.selectedYear = null;
            this.selectedMake = null;
            this.selectedModel = null;
            this.isPickerOpen = false;
            this.isGarageOpen = false;
            this.render();
        });
    }

    private renderPicker(): void {
        this.host.innerHTML = `
            <div class="sf-vehicle-picker">
                <div class="sf-vehicle-picker-row">
                    <select class="sf-vehicle-select" data-field="year" aria-label="Year">
                        <option value="">${this.isLoadingYears ? 'Loading…' : 'Year'}</option>
                        ${this.years.map(y => `<option value="${y}"${y === this.selectedYear ? ' selected' : ''}>${y}</option>`).join('')}
                    </select>
                    <select class="sf-vehicle-select" data-field="make" aria-label="Make"${!this.selectedYear || this.isLoadingMakes ? ' disabled' : ''}>
                        <option value="">${this.isLoadingMakes ? 'Loading…' : 'Make'}</option>
                        ${this.makes.map(m => `<option value="${this.escapeAttr(m)}"${m === this.selectedMake ? ' selected' : ''}>${this.escape(m)}</option>`).join('')}
                    </select>
                    <select class="sf-vehicle-select" data-field="model" aria-label="Model"${!this.selectedMake || this.isLoadingModels ? ' disabled' : ''}>
                        <option value="">${this.isLoadingModels ? 'Loading…' : 'Model'}</option>
                        ${this.models.map(m => `<option value="${this.escapeAttr(m)}"${m === this.selectedModel ? ' selected' : ''}>${this.escape(m)}</option>`).join('')}
                    </select>
                    <button type="button" class="sf-vehicle-go" data-action="apply"${!(this.selectedYear && this.selectedMake && this.selectedModel) ? ' disabled' : ''}>Go</button>
                </div>
                <div class="sf-vehicle-picker-vin">
                    <span class="sf-vehicle-vin-label">or VIN</span>
                    <input type="text" class="sf-vehicle-vin-input" data-field="vin" maxlength="17" placeholder="17-character VIN" aria-label="VIN" />
                    <button type="button" class="sf-vehicle-vin-decode" data-action="decode-vin"${this.isDecodingVin ? ' disabled' : ''}>${this.isDecodingVin ? 'Decoding…' : 'Decode'}</button>
                </div>
                <div class="sf-garage-chips-slot"></div>
            </div>
        `;
        this.renderGarageChips();

        // Year change → load makes
        this.host.querySelector('[data-field="year"]')?.addEventListener('change', (e) => {
            const v = (e.target as HTMLSelectElement).value;
            this.selectedYear = v ? parseInt(v, 10) : null;
            this.selectedMake = null;
            this.selectedModel = null;
            this.makes = [];
            this.models = [];
            this.render();
            if (this.selectedYear) this.loadMakes(this.selectedYear);
        });

        // Make change → load models
        this.host.querySelector('[data-field="make"]')?.addEventListener('change', (e) => {
            this.selectedMake = (e.target as HTMLSelectElement).value || null;
            this.selectedModel = null;
            this.models = [];
            this.render();
            if (this.selectedYear && this.selectedMake) this.loadModels(this.selectedYear, this.selectedMake);
        });

        // Model change
        this.host.querySelector('[data-field="model"]')?.addEventListener('change', (e) => {
            this.selectedModel = (e.target as HTMLSelectElement).value || null;
            this.render();
        });

        // Apply (Go button) — write the cookie and collapse to pill
        this.host.querySelector('[data-action="apply"]')?.addEventListener('click', () => {
            if (this.selectedYear && this.selectedMake && this.selectedModel) {
                this.applyYmm(this.selectedYear, this.selectedMake, this.selectedModel);
            }
        });

        // Decode VIN
        this.host.querySelector('[data-action="decode-vin"]')?.addEventListener('click', () => {
            const input = this.host.querySelector('[data-field="vin"]') as HTMLInputElement;
            const vin = input?.value?.trim() || '';
            if (vin) this.decodeVin(vin);
        });

        // Enter key on VIN field triggers decode
        this.host.querySelector('[data-field="vin"]')?.addEventListener('keydown', (e) => {
            if ((e as KeyboardEvent).key === 'Enter') {
                e.preventDefault();
                const input = e.target as HTMLInputElement;
                if (input.value.trim()) this.decodeVin(input.value.trim());
            }
        });

        if (this.years.length === 0 && !this.isLoadingYears) {
            this.loadYears();
        }
    }

    // ── Data fetches ────────────────────────────────────────

    private async loadYears(): Promise<void> {
        if (this.isLoadingYears) return;
        this.isLoadingYears = true;
        this.render();
        try {
            const res = await fetch('/Services/Storefront/Vehicle/Years', { method: 'POST' });
            const data = await res.json();
            this.years = (data?.Years ?? []) as number[];
        } catch {
            this.years = [];
        } finally {
            this.isLoadingYears = false;
            // If we already had a selected year (e.g. coming from "change"),
            // re-trigger downstream loads now that years are populated.
            if (this.selectedYear && this.makes.length === 0) {
                this.loadMakes(this.selectedYear);
            } else {
                this.render();
            }
        }
    }

    private async loadMakes(year: number): Promise<void> {
        if (this.isLoadingMakes) return;
        this.isLoadingMakes = true;
        this.render();
        try {
            const res = await fetch(`/Services/Storefront/Vehicle/Makes?year=${year}`, { method: 'POST' });
            const data = await res.json();
            this.makes = (data?.Makes ?? []) as string[];
        } catch {
            this.makes = [];
        } finally {
            this.isLoadingMakes = false;
            if (this.selectedMake && this.models.length === 0 && this.selectedYear) {
                this.loadModels(this.selectedYear, this.selectedMake);
            } else {
                this.render();
            }
        }
    }

    private async loadModels(year: number, make: string): Promise<void> {
        if (this.isLoadingModels) return;
        this.isLoadingModels = true;
        this.render();
        try {
            const url = `/Services/Storefront/Vehicle/Models?year=${year}&make=${encodeURIComponent(make)}`;
            const res = await fetch(url, { method: 'POST' });
            const data = await res.json();
            this.models = (data?.Models ?? []) as string[];
        } catch {
            this.models = [];
        } finally {
            this.isLoadingModels = false;
            this.render();
        }
    }

    private async decodeVin(vin: string): Promise<void> {
        if (this.isDecodingVin) return;
        this.isDecodingVin = true;
        this.render();
        try {
            const url = `/Services/Storefront/Vehicle/DecodeVin?vin=${encodeURIComponent(vin)}`;
            const res = await fetch(url, { method: 'POST' });
            const data = await res.json();
            if (data?.Error) {
                // DecodeVin returns Error as a string; a thrown server error comes back as
                // Serenity's { Code, Message } object instead.
                showToast(typeof data.Error === 'string' ? data.Error : (data.Error.Message || 'VIN could not be decoded.'), 'error');
                return;
            }
            if (data?.Year && data?.Make && data?.Model) {
                this.applyVin(data);
            } else {
                showToast('VIN could not be decoded. Please check the 17 characters and try again.', 'error');
            }
        } catch {
            showToast('VIN lookup failed. Please try again.', 'error');
        } finally {
            this.isDecodingVin = false;
            this.render();
        }
    }

    // ── My Garage (#36990, signed-in only) ──────────────────

    private async loadGarage(): Promise<void> {
        try {
            const data = await fetchGarage();
            // RequiresLogin also covers a signed-in account with no customer (staff): no garage.
            this.garage = data?.RequiresLogin ? null : (data?.Vehicles ?? []);
            this.garageMax = data?.MaxVehicles || this.garageMax;
        } catch {
            this.garage = null;
        }
        // In picker mode only the chips change — a full render would wipe a half-typed VIN.
        if (this.vehicle && !this.isPickerOpen) this.render();
        else this.renderGarageChips();
    }

    private closeGarage(): void {
        this.isGarageOpen = false;
        this.garageError = null;
        this.render();
    }

    private garagePanelHtml(saved: GarageVehicle | null): string {
        const list = this.garage ?? [];
        const full = list.length >= this.garageMax;
        const v = this.vehicle!;

        let saveSection: string;
        if (saved) {
            saveSection = `<div class="sf-garage-panel-note"><i class="fa fa-check"></i> Saved as <strong>${this.escape(garageLabel(saved))}</strong></div>`;
        } else if (full) {
            saveSection = `<div class="sf-garage-panel-note is-warn"><i class="fa fa-exclamation-circle"></i> Your garage is full (${list.length} of ${this.garageMax} vehicles). Remove one in <a href="${sfUrl('/account/garage')}">Manage garage</a> to save this vehicle.</div>`;
        } else {
            saveSection = `
                <form class="sf-garage-save" data-action="save-form">
                    <label class="sf-garage-panel-h" for="sf-garage-nickname">Save ${this.escape(`${v.year} ${v.make} ${v.model}`)} to My Garage</label>
                    <div class="sf-garage-save-row">
                        <input type="text" id="sf-garage-nickname" data-field="nickname" maxlength="50" placeholder="Nickname (optional), e.g. Work truck" autocomplete="off" />
                        <button type="submit" class="sf-btn sf-btn-sm"${this.isSaving ? ' disabled' : ''}>${this.isSaving ? 'Saving…' : 'Save'}</button>
                    </div>
                </form>`;
        }

        const items = list.map(g => {
            const current = isSameVehicle(v, g);
            return `
                <button type="button" class="sf-garage-item${current ? ' is-current' : ''}" data-garage-id="${g.Id}"${current ? ' aria-current="true"' : ''}>
                    <i class="fa fa-car"></i>
                    <span class="sf-garage-item-text">
                        <span class="sf-garage-item-t">${this.escape(garageLabel(g))}</span>
                        ${g.Nickname ? `<span class="sf-garage-item-d">${this.escape(g.Name)}</span>` : ''}
                    </span>
                    ${current ? '<span class="sf-garage-item-badge">Current</span>' : ''}
                </button>`;
        }).join('');

        return `
            <div class="sf-garage-panel" role="dialog" aria-label="My Garage">
                ${saveSection}
                ${this.garageError ? `<div class="sf-garage-panel-note is-err">${this.escape(this.garageError)}</div>` : ''}
                <div class="sf-garage-panel-h">My Garage <span class="sf-garage-count">${list.length} of ${this.garageMax}</span></div>
                ${items ? `<div class="sf-garage-list">${items}</div>` : '<div class="sf-garage-empty">No saved vehicles yet.</div>'}
                <a class="sf-garage-manage" href="${sfUrl('/account/garage')}">Manage garage <i class="fa fa-angle-right"></i></a>
            </div>`;
    }

    private wireGaragePanel(): void {
        this.host.querySelector('[data-action="save-form"]')?.addEventListener('submit', e => {
            e.preventDefault();
            const input = this.host.querySelector('[data-field="nickname"]') as HTMLInputElement | null;
            this.saveCurrent(input?.value.trim() ?? '');
        });
        this.wireGarageSwitch(this.host.querySelector('.sf-garage-panel'));
    }

    /** Saved vehicles as chips under the picker — shown when no vehicle is selected (or while changing). */
    private renderGarageChips(): void {
        const slot = this.host.querySelector('.sf-garage-chips-slot') as HTMLElement | null;
        if (!slot) return;
        const list = this.garage ?? [];
        if (list.length === 0) {
            slot.innerHTML = '';
            return;
        }
        slot.innerHTML = `
            <div class="sf-garage-chips">
                <span class="sf-vehicle-vin-label">My Garage</span>
                ${list.map(g => `<button type="button" class="sf-garage-chip${isSameVehicle(this.vehicle, g) ? ' is-current' : ''}" data-garage-id="${g.Id}" title="${this.escapeAttr(g.Name)}"><i class="fa fa-car"></i> ${this.escape(garageLabel(g))}</button>`).join('')}
                <a class="sf-garage-chips-manage" href="${sfUrl('/account/garage')}">Manage</a>
            </div>`;
        this.wireGarageSwitch(slot);
    }

    private wireGarageSwitch(root: Element | null): void {
        root?.querySelectorAll('[data-garage-id]').forEach(btn => btn.addEventListener('click', () => {
            this.switchTo(parseInt(btn.getAttribute('data-garage-id') || '0', 10));
        }));
    }

    /** Same as picking it with Go: write the cookie, then the catalog filters by it. */
    private async switchTo(id: number): Promise<void> {
        const g = this.garage?.find(x => x.Id === id);
        if (!g) return;
        const v = toCookieVehicle(g);
        writeVehicleCookie(v);
        this.vehicle = v;
        this.isPickerOpen = false;
        this.isGarageOpen = false;
        this.render();
        try {
            await useGarageVehicle(id); // keeps the garage list in most-recently-used order
        } catch {
            // Only the ordering is lost.
        }
        this.goToCatalog();
    }

    private async saveCurrent(nickname: string): Promise<void> {
        if (!this.vehicle || this.isSaving) return;
        this.isSaving = true;
        this.garageError = null;
        this.render();
        try {
            const res = await saveToGarage(this.vehicle, nickname);
            if (res.RequiresLogin) {
                window.location.href = sfUrl('/account/login');
                return;
            }
            if (!res.Success) {
                this.garageError = res.Error || 'Could not save this vehicle. Please try again.';
                if (res.LimitReached) await this.loadGarage();
                return;
            }
            showToast(res.AlreadySaved ? 'This vehicle is already in My Garage.' : 'Saved to My Garage.', 'success');
            this.isGarageOpen = false;
            // saveToGarage fired sc-garage-changed, which reloads the list and re-renders.
        } catch {
            this.garageError = 'Could not save this vehicle. Please try again.';
        } finally {
            this.isSaving = false;
            this.render();
        }
    }

    // ── Apply (write cookie) ────────────────────────────────

    private applyYmm(year: number, make: string, model: string): void {
        const v: StorefrontVehicle = {
            source: 'ymm',
            year,
            make,
            model,
            setAt: new Date().toISOString()
        };
        writeVehicleCookie(v);
        this.vehicle = v;
        this.isPickerOpen = false;
        this.render();
        this.goToCatalog();
    }

    /**
     * Take the shopper to the catalog showing parts that fit the selected vehicle.
     * If already on the catalog, writeVehicleCookie's sc-vehicle-changed event has
     * re-filtered it in place, so no navigation is needed.
     */
    private goToCatalog(): void {
        if (!window.location.pathname.toLowerCase().startsWith(sfUrl('/catalog')))
            window.location.href = sfUrl('/catalog');
    }

    private applyVin(data: { Vin: string; Year: number; Make: string; Model: string; Trim?: string; Engine?: string }): void {
        const v: StorefrontVehicle = {
            source: 'vin',
            year: data.Year,
            make: data.Make,
            model: data.Model,
            trim: data.Trim || null,
            engine: data.Engine || null,
            vin: data.Vin,
            setAt: new Date().toISOString()
        };
        writeVehicleCookie(v);
        this.vehicle = v;
        this.isPickerOpen = false;
        this.render();
        this.goToCatalog();
    }

    // ── HTML escape helpers ─────────────────────────────────

    private escape(s: string): string {
        return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
    }

    private escapeAttr(s: string): string {
        return this.escape(s);
    }
}
